<template>
  <v-row>
    <v-col cols="5" v-if="isFetched">
      <v-row no-gutters class="pb-5">
        <base-button
          :disabled="$attrs.disabled"
          @click="openAddCategoryDialog"
          translation-path="button.addCategory"
          class="mr-0"
        ></base-button>
      </v-row>
      <v-row
        no-gutters
        class="border align-center element justify-space-between flex-nowrap px-4"
        style="background-color: white"
        v-for="category in categories"
        :key="category.id"
      >
        <v-col cols="10">
          <div
            class="text-left text-truncate text-body-2 flex-shrink-0 py-2"
            :class="$attrs.disabled ? 'text--disabled' : 'text--black'"
          >
            {{ category.name }}
          </div>
        </v-col>
        <v-col cols="auto">
          <dots-menu
            :disabled="$attrs.disabled"
            font-size="19px"
            :items="computedItems"
            @changeName="openEditNameDialog(category)"
            @delete="openDeleteDialog(category.id)"
          >
          </dots-menu>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="5" v-else>
      <main-loader></main-loader>
    </v-col>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteCategory')"
      :body="$i18n.t('dialog.body.deleteCategoryConfirmation', { name })"
      @agree="handleCategoryDelete"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
    <main-input-dialog
      v-if="isAddDialogLoaded"
      :inputs="addNewCategoryInputs"
      :is-valid="isNameValid"
      :is-visible.sync="isAddDialogVisible"
      :loading="isConfirmButtonLoading"
      dialog-width="45%"
      :title-translation="$i18n.t('dialog.title.addNewCategory')"
      @agree="handleCategoryAdd"
      agree-text="dialog.agreeSave"
      header-shadow
    />
    <main-dialog
      v-if="isEditNameDialogLoaded"
      :is-visible.sync="isEditNameDialogVisible"
      :title="$i18n.t('dialog.title.changeName')"
      @agree="handleCategoryEditName"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeSave"
      header-shadow
      modal-width="45%"
      :is-valid="isNameValid"
      body-padding="16px"
      underlineFooter
      :is-buttons-right="false"
    >
      <template #body>
        <main-input-dialog-row
          name="name"
          translation-path="dialog.name"
          :data-type="dataType"
          clearable
          cols="12"
          v-model="name"
        ></main-input-dialog-row>
      </template>
    </main-dialog>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'InvestmentCategories',
  components: {
    MainInputDialogRow,
    MainLoader,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isFetched: false,
    name: '',
    dataType: { name: 'text' },
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isAddDialogVisible: false,
    isAddDialogLoaded: false,
    isEditNameDialogLoaded: false,
    isEditNameDialogVisible: false,
    isConfirmButtonLoading: false,
    categoryToEdit: undefined,
    categoryToDelete: undefined,
  }),
  computed: {
    computedItems() {
      if (this.categories.length < 1) {
        return [
          {
            name: 'changeName',
          },
        ];
      } else {
        return [
          {
            name: 'changeName',
          },
          {
            name: 'delete',
          },
        ];
      }
    },
    categories: get('investments/categories'),
    isNameValid() {
      if (this.name === null) {
        return false;
      } else {
        return !(this.name.trim() === '' || this.name === undefined);
      }
    },
    addNewCategoryInputs() {
      return [
        {
          name: 'name',
          hint: this.$i18n.t('dialog.writeDefaultCategoryName'),
          rules: [v => !!v || 'rules.required'],
          translationPath: 'dialog.body.name',
          persistentHint: true,
          cols: '12',
          dataType: {
            name: 'text',
          },
        },
      ];
    },
  },
  methods: {
    getCategories: call('investments/getCategories'),
    addCategory: call('investments/addCategory'),
    editCategory: call('investments/editCategory'),
    deleteCategory: call('investments/deleteCategory'),

    openAddCategoryDialog() {
      this.isAddDialogLoaded = true;
      this.isAddDialogVisible = true;
    },
    getCategoryName(id) {
      const s = this.categories.find(l => l.id === id);
      return s.name;
    },
    openDeleteDialog(id) {
      this.name = this.getCategoryName(id);
      this.categoryToDelete = id;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async openEditNameDialog(category) {
      this.categoryToEdit = category;
      this.name = this.categoryToEdit.name;
      this.isEditNameDialogLoaded = true;
      this.isEditNameDialogVisible = true;
    },
    async handleCategoryEditName() {
      if (this.categories.filter(cat => cat.name !== this.categoryToEdit.name).some(cat => cat.name === this.name)) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.categoryAlreadyExists'),
        });
        this.isConfirmButtonLoading = false;
        return;
      }
      try {
        this.isConfirmButtonLoading = true;
        await this.editCategory({
          data: {
            name: this.name,
          },
          category_id: this.categoryToEdit.id,
        });
        this.categoryToEdit = undefined;
        this.name = '';
        this.isConfirmButtonLoading = false;
      } catch (error) {
        this.isEditNameDialogLoaded = false;
      }
      this.isEditNameDialogVisible = false;
    },
    async handleCategoryAdd() {
      this.isConfirmButtonLoading = true;
      const newName = this.addNewCategoryInputs.find(input => input.name === 'name').value;
      if (this.categories.some(cat => cat.name === newName)) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.categoryAlreadyExists'),
        });
        this.isConfirmButtonLoading = false;
        return;
      }
      try {
        await this.addCategory({
          name: newName,
        });
      } finally {
        this.name = '';
        this.isAddDialogLoaded = false;
        this.isAddDialogVisible = false;
        this.isConfirmButtonLoading = false;
      }
    },
    async handleCategoryDelete() {
      this.isConfirmButtonLoading = true;
      try {
        await this.deleteCategory(this.categoryToDelete);
        this.categoryToDelete = undefined;
        this.isConfirmButtonLoading = false;
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
      } catch (e) {
        console.log(e);
        this.categoryToDelete = undefined;
        this.isConfirmButtonLoading = false;
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
      }
    },
    async init() {
      this.isFetched = false;
      await this.getCategories();
      this.isFetched = true;
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
</style>
